import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InputField from "../components/InputField";
import Button from "../components/Button";
import { sendOtp } from "../utils/sendOtp";
import { useAuth } from "../hook/useAuth";
import { clearPhoneRecaptcha, initPhoneRecaptcha } from "../utils/firebasePhoneAuth";
import './Auth.css'

const Auth: React.FC = () => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [isSending, setIsSending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useAuth();

  const getPhoneAuthErrorMessage = (err: any) => {
    if (err?.code === "auth/too-many-requests") {
      return "Too many requests. Please try again later.";
    }

    if (err?.code === "auth/invalid-phone-number") {
      return "Please enter a valid phone number.";
    }

    if (err?.code === "auth/missing-recaptcha-token") {
      return "Verification failed. Please try again.";
    }

    if (err?.code === "auth/invalid-app-credential") {
      return "We could not send the OTP right now. Please try again in a moment.";
    }

    if (err?.message) {
      return "We could not send the OTP right now. Please try again.";
    }

    return "Failed to send OTP. Please try again.";
  };

  useEffect(() => {
    const initializeRecaptcha = async () => {
      try {
        await initPhoneRecaptcha({
          containerId: "recaptcha-container-auth",
          size: "invisible",
        });
      } catch (error) {
        console.error("Error initializing reCAPTCHA on auth screen:", error);
      }
    };

    initializeRecaptcha();

    return () => {
      clearPhoneRecaptcha();
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      let returnTo = location.state?.returnTo || "/";
      navigate(returnTo, { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = mobileNumber.trim();

    if (!/^\d{10}$/.test(trimmed)) {
      return;
    }

    // Ensure it starts with valid Indian mobile prefixes (optional but good practice)
    const firstDigit = trimmed.charAt(0);
    if (!['6', '7', '8', '9'].includes(firstDigit)) {
      return;
    }

    const fullNumber = `+91${trimmed}`;
    console.log("Sending OTP to formatted number:", fullNumber);

    setIsSending(true);

    try {
      const confirmation = await sendOtp(fullNumber, "recaptcha-container-auth");
      window.confirmationResult = confirmation;
      clearPhoneRecaptcha();
      console.log("OTP sent successfully, navigating to OTP page");
      navigate("/auth/otp", { state: { mobileNumber: fullNumber, returnTo: location.state?.returnTo} });
    } catch (err: any) {
      console.error("Failed to send OTP:", {
        code: err?.code,
        message: err?.message,
        err,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className = "login-container">

      <div className="login-form-wrapper">
        <h2 className="form-main-title">Verify Mobile</h2>

        <form onSubmit={handleSendOtp} className="expert-form-stack">
          <InputField
            type="number"
            label="Mobile Number"
            value={mobileNumber}
            onChange={setMobileNumber}
            maxLength={10}
          />
        
          <div id="recaptcha-container-auth" style={{ marginTop: '18px', display: 'flex', justifyContent: 'center' }} />
        
          <Button type="submit" variant="primary" disabled={isSending} className="full-width-btn">
            {isSending ? "Processing..." : "Get Verification Code"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Auth;
